import React, { useState, useEffect } from 'react';
import { X, Instagram, Youtube, Eye } from 'lucide-react';
import { getCreators } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const CreatorsGallery = () => {
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCreators = async () => {
      try {
        setLoading(true);
        const data = await getCreators();
        setCreators(data);
      } catch (error) {
        console.error('Error loading creators:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCreators();
  }, []);

  if (loading) {
    return (
      <section id="creators" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#0077cc] border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading creators...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="creators" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-[#0077cc]">Creators Network</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Meet the talented creators powering our content ecosystem
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {creators.map((creator, index) => (
            <div
              key={creator.id || index}
              className="group relative cursor-pointer overflow-hidden rounded-2xl"
              onClick={() => setSelectedCreator(creator)}
            >
              <div className="aspect-square">
                <img
                  src={creator.image}
                  alt={creator.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg mb-1">{creator.name}</h3>
                    <p className="text-white/80 text-sm mb-2">{creator.category}</p>
                    <div className="flex items-center text-white/90 text-sm">
                      <Instagram size={14} className="mr-1" />
                      {creator.subscribers} Followers
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2">
                    <Eye size={20} className="text-white" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Creator Detail Modal */}
      <Dialog open={!!selectedCreator} onOpenChange={() => setSelectedCreator(null)}>
        <DialogContent className="max-w-md">
          {selectedCreator && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{selectedCreator.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <img
                  src={selectedCreator.image}
                  alt={selectedCreator.name}
                  className="w-full h-64 object-cover rounded-xl"
                />
                <div>
                  <p className="text-gray-600 font-medium mb-4">{selectedCreator.category}</p>
                  
                  <div className="grid grid-cols-1 gap-4 mb-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Instagram Followers</p>
                      <p className="text-2xl font-bold text-[#0077cc]">{selectedCreator.subscribers}</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <a
                      href={`https://instagram.com/${selectedCreator.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-full hover:shadow-lg transition-shadow"
                    >
                      <Instagram size={20} />
                      Follow on Instagram
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CreatorsGallery;
